import { useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  FileText,
  Key,
  Landmark,
  Pencil,
  QrCode,
  Receipt,
  Trash2,
  Truck,
  User,
  UserCheck,
  Wallet,
  XCircle,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/hooks/useAuth";
import { useSolicitacaoPagamentoAccess } from "@/hooks/useSolicitacaoPagamentoAccess";
import { formatCpfCnpjInput } from "@/utils/brDocument";
import { formatCurrency, formatDate, formatDateTime } from "@/utils/format";
import {
  aprovarSolicitacaoFinanceira,
  aprovarSolicitacaoOperacional,
  cancelarSolicitacaoPagamento,
  canUserCancelSolicitacao,
  finalizarTituloSienge,
  fetchTituloSiengePreview,
  reprovarSolicitacaoFinanceira,
  reprovarSolicitacaoOperacional,
  STATUS_LABELS,
  type SiengeBillPreview,
  type SolicitacaoPagamentoDto,
} from "@/services/solicitacaoPagamento";
import { SolicitacaoPagamentoBillPreviewModal } from "./SolicitacaoPagamentoBillPreviewModal";
import { SolicitacaoPagamentoTimelineModal } from "./SolicitacaoPagamentoTimelineModal";
import { SolicitacaoPagamentoReprovarModal } from "./SolicitacaoPagamentoReprovarModal";
import { SolicitacaoPagamentoCancelarModal } from "./SolicitacaoPagamentoCancelarModal";
import { SolicitacaoPagamentoAnexoModal } from "./SolicitacaoPagamentoAnexoModal";
import { renderDadosPagamentoRows } from "./pagamentoMetodoDisplay";
import { PaymentRequestPdfButtons } from "./pdf/PaymentRequestPdfButtons";

export type SolicitacaoListMode = "minhas" | "operacional" | "financeira" | "sienge" | "arquivadas";

function StatusBadge({ status }: { status: SolicitacaoPagamentoDto["status"] }) {
  if (status === "aprovada") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">
        <CheckCircle2 className="w-3.5 h-3.5" /> {STATUS_LABELS[status]}
      </span>
    );
  }
  if (status === "reprovada") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-3 py-1 rounded-full">
        <XCircle className="w-3.5 h-3.5" /> {STATUS_LABELS[status]}
      </span>
    );
  }
  if (status === "cancelada") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-100 border border-slate-200 px-3 py-1 rounded-full">
        <XCircle className="w-3.5 h-3.5" /> {STATUS_LABELS[status]}
      </span>
    );
  }
  if (status === "aguardando_sienge") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 bg-violet-50 border border-violet-200 px-3 py-1 rounded-full">
        <Clock className="w-3.5 h-3.5" /> {STATUS_LABELS[status]}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
      <Clock className="w-3.5 h-3.5" /> {STATUS_LABELS[status]}
    </span>
  );
}

function DetailField({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="flex gap-3 py-2.5">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center">
        <Icon className="w-4 h-4 text-brand-600" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-brand-600">{label}</p>
        <p className="text-sm text-slate-800 break-words mt-0.5">{value || "—"}</p>
      </div>
    </div>
  );
}

const PAYMENT_FIELD_ICONS: Record<string, LucideIcon> = {
  "Tipo chave": Key,
  "Chave Pix": QrCode,
  "Conta em nome": User,
  "Código de barras": FileText,
  Titular: User,
  Banco: Landmark,
  Agência: Building2,
  Conta: Wallet,
};

interface Props {
  open: boolean;
  onClose: () => void;
  item: SolicitacaoPagamentoDto;
  mode: SolicitacaoListMode;
  onAction: () => void;
  onEdit?: (item: SolicitacaoPagamentoDto) => void;
  canFinalizarSienge?: boolean;
}

export function SolicitacaoPagamentoDetailModal({
  open,
  onClose,
  item,
  mode,
  onAction,
  onEdit,
  canFinalizarSienge = false,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState<SiengeBillPreview | null>(null);
  const [siengeBillIdResult, setSiengeBillIdResult] = useState<string | null>(null);
  const [pendingFinalize, setPendingFinalize] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [reprovarOpen, setReprovarOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [anexoOpen, setAnexoOpen] = useState(false);

  const { user } = useAuth();
  const { canAprovarOperacional, canAprovarFinanceira, hasFullAccess } =
    useSolicitacaoPagamentoAccess();

  const hasTimeline = (item.timeline?.length ?? 0) > 0;
  const showOperacionalActions =
    item.status === "pendente_aprovacao" &&
    (mode === "operacional" || (hasFullAccess && canAprovarOperacional));
  const showFinanceiraActions =
    item.status === "pendente_financeira" &&
    (mode === "financeira" || (hasFullAccess && canAprovarFinanceira));
  const showSiengeFinalize =
    item.status === "aguardando_sienge" &&
    (hasFullAccess || canAprovarFinanceira || canFinalizarSienge);
  const showSiengeReprovarActions =
    item.status === "aguardando_sienge" &&
    !item.siengeBillId &&
    (hasFullAccess || canAprovarFinanceira || canAprovarOperacional);
  const canFinalizeModal =
    (hasFullAccess || canAprovarFinanceira || canFinalizarSienge) &&
    (showSiengeFinalize || pendingFinalize) &&
    !siengeBillIdResult;
  const canEdit =
    item.status === "reprovada" && onEdit && (mode === "minhas" || hasFullAccess);
  const showCancelAction =
    mode !== "arquivadas" &&
    canUserCancelSolicitacao(item, user?.id ?? "", {
      isAdmin: hasFullAccess,
      canAprovarOperacional,
      canAprovarFinanceira,
    });

  const paymentRows = renderDadosPagamentoRows(item);
  const siengeBillId = siengeBillIdResult ?? item.siengeBillId;

  const handleAprovar = async () => {
    setLoading(true);
    setError(null);
    try {
      if (mode === "financeira") {
        const result = await aprovarSolicitacaoFinanceira(item.id);
        onClose();
        setPreview(result.billPreview);
        setSiengeBillIdResult(null);
        setPendingFinalize(true);
        setPreviewOpen(true);
      } else {
        await aprovarSolicitacaoOperacional(item.id);
        onClose();
      }
      onAction();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Erro ao aprovar.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleReprovar = async (motivo: string) => {
    setLoading(true);
    setError(null);
    try {
      if (mode === "financeira" || item.status === "aguardando_sienge") {
        await reprovarSolicitacaoFinanceira(item.id, motivo);
      } else {
        await reprovarSolicitacaoOperacional(item.id, motivo);
      }
      setReprovarOpen(false);
      onClose();
      onAction();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Erro ao reprovar.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPreview = async (forFinalize = false) => {
    setLoading(true);
    setError(null);
    try {
      const p = await fetchTituloSiengePreview(item.id);
      setPreview(p);
      if (forFinalize) setSiengeBillIdResult(null);
      setPreviewOpen(true);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Erro ao carregar preview.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizeSienge = async (): Promise<string> => {
    const result = await finalizarTituloSienge(item.id);
    setSiengeBillIdResult(result.siengeBillId);
    return result.siengeBillId;
  };

  const handleCancel = async (motivo: string) => {
    setCancelLoading(true);
    setCancelError(null);
    try {
      await cancelarSolicitacaoPagamento(item.id, motivo);
      setCancelOpen(false);
      onClose();
      onAction();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Erro ao cancelar solicitação.";
      setCancelError(msg);
    } finally {
      setCancelLoading(false);
    }
  };

  const modalTitle = (
    <div className="flex items-center gap-2.5 min-w-0">
      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center">
        <FileText className="w-4 h-4 text-brand-600" />
      </div>
      <span className="font-semibold text-slate-800 truncate">{item.descricao || "Solicitação"}</span>
    </div>
  );

  const footer = (
    <div className="flex flex-wrap items-center gap-2">
      {canEdit && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Pencil className="w-4 h-4" />}
          onClick={() => {
            onClose();
            onEdit?.(item);
          }}
        >
          Editar e reenviar
        </Button>
      )}
      {hasTimeline && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Clock className="w-4 h-4" />}
          onClick={() => setTimelineOpen(true)}
        >
          Ver leadtime
        </Button>
      )}
      <PaymentRequestPdfButtons
        item={item}
        geradoPor={user?.displayName?.trim() || user?.username?.trim() || "Usuário"}
      />
      {item.status === "aprovada" && siengeBillId && (
        <span className="inline-flex items-center rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
          Título Sienge #{siengeBillId}
        </span>
      )}
      {showSiengeFinalize && (
        <Button type="button" size="sm" onClick={() => void handleViewPreview(true)} loading={loading}>
          Finalizar envio ao Sienge
        </Button>
      )}
      {item.status === "aprovada" && item.siengeBillPayload && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Eye className="w-4 h-4" />}
          onClick={() => void handleViewPreview()}
          loading={loading}
        >
          Ver título Sienge
        </Button>
      )}
      {(showOperacionalActions || showFinanceiraActions) && (
        <>
          <Button size="sm" onClick={() => void handleAprovar()} loading={loading}>
            Aprovar
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => setReprovarOpen(true)}
            disabled={loading}
          >
            Reprovar
          </Button>
        </>
      )}
      {showSiengeReprovarActions && (
        <Button
          size="sm"
          variant="danger"
          onClick={() => setReprovarOpen(true)}
          disabled={loading}
        >
          Reprovar
        </Button>
      )}
      {showCancelAction && (
        <Button
          type="button"
          size="sm"
          variant="danger"
          leftIcon={<Trash2 className="w-4 h-4" />}
          onClick={() => setCancelOpen(true)}
          disabled={loading || cancelLoading}
        >
          Cancelar solicitação
        </Button>
      )}
    </div>
  );

  return (
    <>
      <Modal open={open} onClose={onClose} title={modalTitle} size="lg" footer={footer}>
        <div className="space-y-5 -mt-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <h2 className="text-2xl font-bold text-slate-900 leading-tight">
              {item.descricao || "Solicitação de pagamento"}
            </h2>
            <StatusBadge status={item.status} />
          </div>

          <p className="flex items-center gap-2 text-sm text-slate-500">
            <Receipt className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span>
              {formatCurrency(item.valor)} · {item.formaPagamento}
              {item.dueDate ? ` · Venc. ${formatDate(item.dueDate)}` : ""}
            </span>
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
            <div>
              <DetailField icon={User} label="Solicitante" value={item.solicitanteName} />
              <DetailField icon={Building2} label="Centro de custo" value={item.costCenterLabel} />
              <DetailField icon={Truck} label="Fornecedor" value={item.creditorName} />
              <DetailField
                icon={FileText}
                label="CPF / CNPJ"
                value={formatCpfCnpjInput(item.creditorDocument)}
              />
              <DetailField
                icon={Calendar}
                label="Criada em"
                value={formatDateTime(item.createdAt)}
              />
              {paymentRows.map((row) => (
                <DetailField
                  key={row.label}
                  icon={PAYMENT_FIELD_ICONS[row.label] ?? Key}
                  label={row.label}
                  value={row.value}
                />
              ))}
            </div>
            <div>
              <DetailField icon={UserCheck} label="Aprovador operacional" value={item.aprovadorName} />
              <DetailField icon={Wallet} label="Plano financeiro" value={item.paymentCategoryLabel} />
              <DetailField
                icon={FileText}
                label="Tipo documento"
                value={item.documentIdentificationId?.trim() || "REC"}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
            {item.anexoOriginalName && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-brand-600 mb-2">
                  Anexo
                </p>
                <button
                  type="button"
                  onClick={() => setAnexoOpen(true)}
                  className="w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left hover:bg-slate-100 hover:border-slate-300 transition-colors"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-red-500" />
                  </div>
                  <span className="flex-1 min-w-0 text-sm font-medium text-slate-800 truncate">
                    {item.anexoOriginalName}
                  </span>
                  <Download className="w-4 h-4 text-slate-400 flex-shrink-0" />
                </button>
              </div>
            )}
            <div className={item.anexoOriginalName ? "" : "md:col-span-2"}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-brand-600 mb-2">
                Descrição / Observação
              </p>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 min-h-[72px]">
                <p className="text-sm text-slate-700 whitespace-pre-wrap">
                  {item.observacao?.trim() || "—"}
                </p>
              </div>
            </div>
          </div>

          {(item.reviewNote || item.financeiroReviewNote || item.cancelNote) && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 space-y-1">
              {item.reviewNote && (
                <p>
                  <span className="font-semibold text-slate-700">Parecer operacional:</span>{" "}
                  {item.reviewNote}
                </p>
              )}
              {item.financeiroReviewNote && (
                <p>
                  <span className="font-semibold text-slate-700">Parecer financeiro:</span>{" "}
                  {item.financeiroReviewNote}
                </p>
              )}
              {item.cancelNote && (
                <p>
                  <span className="font-semibold text-slate-700">Motivo do cancelamento:</span>{" "}
                  {item.cancelNote}
                  {item.canceladaByName ? ` (${item.canceladaByName})` : ""}
                </p>
              )}
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      </Modal>

      <SolicitacaoPagamentoTimelineModal
        open={timelineOpen}
        onClose={() => setTimelineOpen(false)}
        item={item}
      />

      {item.anexoOriginalName && (
        <SolicitacaoPagamentoAnexoModal
          open={anexoOpen}
          onClose={() => setAnexoOpen(false)}
          solicitacaoId={item.id}
          fileName={item.anexoOriginalName}
        />
      )}

      <SolicitacaoPagamentoReprovarModal
        open={reprovarOpen}
        onClose={() => setReprovarOpen(false)}
        onConfirm={handleReprovar}
        loading={loading}
      />

      <SolicitacaoPagamentoCancelarModal
        open={cancelOpen}
        onClose={() => {
          setCancelOpen(false);
          setCancelError(null);
        }}
        onConfirm={handleCancel}
        loading={cancelLoading}
        error={cancelError}
      />

      <SolicitacaoPagamentoBillPreviewModal
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPendingFinalize(false);
          if (siengeBillIdResult) onAction();
        }}
        preview={preview}
        solicitacaoId={item.id}
        canFinalize={canFinalizeModal}
        siengeBillId={item.status === "aprovada" ? siengeBillId : null}
        onFinalize={handleFinalizeSienge}
      />
    </>
  );
}
